import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface AuditLog {
  id: string;
  user_id: string | null;
  action: string;
  resource_type: string;
  resource_id: string;
  resource_name: string | null;
  changes: Record<string, unknown> | null;
  context: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

const ACTION_COLORS: Record<string, string> = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  read: 'bg-gray-100 text-gray-800',
  login: 'bg-purple-100 text-purple-800',
  logout: 'bg-purple-100 text-purple-800',
  'auth.login': 'bg-purple-100 text-purple-800',
  'auth.logout': 'bg-purple-100 text-purple-800',
  'auth.register': 'bg-green-100 text-green-800',
  'admin.user_created': 'bg-green-100 text-green-800',
  'admin.user_updated': 'bg-blue-100 text-blue-800',
  'admin.user_deleted': 'bg-red-100 text-red-800',
  'admin.role_assigned': 'bg-yellow-100 text-yellow-800',
  'admin.role_removed': 'bg-orange-100 text-orange-800',
};

export function AuditLogsTab() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState<string>('');
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    loadLogs();
  }, [actionFilter, limit]);

  async function loadLogs() {
    try {
      const params = new URLSearchParams();
      params.set('limit', limit.toString());
      if (actionFilter) {
        params.set('action', actionFilter);
      }

      const response = await fetch(`/admin/tenant/audit-logs?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  }

  function formatAction(action: string): string {
    return action
      .split(/[._]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  function getActionColor(action: string): string {
    return ACTION_COLORS[action] || 'bg-gray-100 text-gray-800';
  }

  function formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleString();
  }

  function formatContext(context: Record<string, unknown>): string {
    const relevantKeys = ['reason', 'ip', 'extra'];
    const parts: string[] = [];

    for (const key of relevantKeys) {
      if (context[key]) {
        if (key === 'extra' && typeof context[key] === 'object') {
          parts.push(JSON.stringify(context[key]));
        } else {
          parts.push(`${key}: ${context[key]}`);
        }
      }
    }

    return parts.join(', ') || '-';
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Audit Logs</CardTitle>
            <CardDescription>Track all actions in your organization</CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">All Actions</SelectItem>
                <SelectItem value="auth.login">Login</SelectItem>
                <SelectItem value="auth.logout">Logout</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="admin.user_created">User Created</SelectItem>
                <SelectItem value="admin.user_updated">User Updated</SelectItem>
                <SelectItem value="admin.role_assigned">Role Assigned</SelectItem>
              </SelectContent>
            </Select>
            <Select value={limit.toString()} onValueChange={(v) => setLimit(parseInt(v))}>
              <SelectTrigger className="w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25 rows</SelectItem>
                <SelectItem value="50">50 rows</SelectItem>
                <SelectItem value="100">100 rows</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => loadLogs()}>
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center text-muted-foreground">Loading audit logs...</div>
        ) : logs.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            No audit logs found.
          </div>
        ) : (
          <div className="rounded-md border">
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium">Time</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Action</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Resource</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">Details</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-muted/50">
                    <td className="px-4 py-3 text-sm">
                      <div className="whitespace-nowrap">{formatTimestamp(log.created_at)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={getActionColor(log.action)}>
                        {formatAction(log.action)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <div className="font-medium capitalize">{log.resource_type}</div>
                        <div className="text-xs text-muted-foreground">
                          {log.resource_name || log.resource_id}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs truncate">
                      {log.changes ? (
                        <span className="text-xs">
                          {log.changes.after
                            ? `Changed: ${Object.keys(log.changes.after as Record<string, unknown>).join(', ')}`
                            : formatContext(log.context)}
                        </span>
                      ) : (
                        formatContext(log.context)
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {log.ip_address || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
